import { productListNoPage } from '@/api/visual'
import { getChartDevice, getChartProduct } from '@/api/device-chart'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { isDeviceChart, chartProperties, numericProperty, responseBody, propertyRows, deviceChartQuery, queryTime } from '@/runtime/device-chart-data'
import { isDeviceValue } from '@/runtime/device-value-data'

// 只为原来的数据面板补充配置行为，不新增 Vue 组件。
export default {
  data () {
    return { chartProperties: [], chartProducts: [], chartMetadataLoading: false, chartMetadataError: '',
      chartProductsLoading: false, chartProductsError: '' }
  },
  computed: {
    activeIsDeviceChart () { return !this.isComponentTemplateEditing && isDeviceChart(this.activeObj) },
    activeIsDeviceValue () { return !this.isComponentTemplateEditing && isDeviceValue(this.activeObj) },
    activeHasDeviceBinding () { return this.activeIsDeviceChart || this.activeIsDeviceValue },
    chartDeviceSource: {
      get () { return this.activeParameter.deviceSource === 'page' ? 'page' : 'fixed' },
      set (value) { this.setChartParameter('deviceSource', value) }
    },
    chartSettingsKey () {
      if (!this.activeHasDeviceBinding) return ''
      return JSON.stringify([this.activeObj.index, this.chartDeviceSource,
        this.chartDeviceSource === 'page' ? this.activeParameter.productId : this.activeParameter.deviceId])
    },
    chartPropertyOptions () {
      const all = (this.activeIsDeviceValue && this.activeObj.component.prop === 'text') || this.activeObj.dataFormatter ||
        (this.activeIsDeviceChart && Number(this.activeParameter.configDataType) === 1 && String(this.activeParameter.aggregationType).toUpperCase() === 'COUNT')
      return this.chartProperties.filter(property => all || numericProperty(property))
    },
    chartRangeMode: {
      get () {
        if (this.activeParameter.rangeMode) return this.activeParameter.rangeMode
        return !this.activeParameter.from || String(this.activeParameter.from).startsWith('now') ? 'relative' : 'fixed'
      },
      set (value) {
        this.setChartParameter('rangeMode', value)
        if (value === 'relative') {
          this.setChartParameter('from', Number(this.activeParameter.configDataType) === 2 ? 'now-1h' : 'now-7d'); this.setChartParameter('to', 'now')
        } else {
          const now = Date.now()
          let from, to
          const start = now - (Number(this.activeParameter.configDataType) === 2 ? 3600000 : 7 * 86400000)
          try { from = queryTime(this.activeParameter.from, start, now); to = queryTime(this.activeParameter.to, now, now) } catch (error) { from = start; to = now }
          this.setChartParameter('from', from); this.setChartParameter('to', to)
        }
      }
    },
    chartFixedFrom: {
      get () { return this.fixedChartTime(this.activeParameter.from) },
      set (value) { this.setChartParameter('from', value || '') }
    },
    chartFixedTo: {
      get () { return this.fixedChartTime(this.activeParameter.to) },
      set (value) { this.setChartParameter('to', value || '') }
    },
    chartTimePreset: {
      get () { return this.activeParameter.from || (Number(this.activeParameter.configDataType) === 2 ? 'now-1h' : 'now-7d') },
      set (value) { this.setChartParameter('from', value); this.setChartParameter('to', 'now') }
    },
    chartDataLimit: {
      get () { return Number(this.activeParameter.limit) || 200 },
      set (value) { this.setChartParameter('limit', value) }
    },
    chartInterval: {
      get () { return this.activeParameter.interval || this.activeParameter.time || '1h' },
      set (value) { this.setChartParameter('interval', value) }
    },
    chartLiveFollow: {
      get () { return this.activeParameter.liveFollow === true },
      set (value) { this.setChartParameter('liveFollow', value) }
    },
    chartRefreshInterval: {
      get () { return Number(this.activeParameter.refreshInterval === undefined ? this.activeObj.time : this.activeParameter.refreshInterval) || 0 },
      set (value) { this.setChartParameter('refreshInterval', value) }
    },
    chartFormatterInput: {
      get () { return this.activeParameter.formatterInput || 'legacy' },
      set (value) { this.setChartParameter('formatterInput', value) }
    },
    chartConfigError () {
      if (!this.activeIsDeviceChart) return ''
      try { deviceChartQuery(this.activeParameter, {}, true); return '' } catch (error) { return error.message }
    }
  },
  watch: {
    chartSettingsKey: { immediate: true, handler () {
      if (this.activeIsDeviceChart && !this.activeObj.parameter) this.$set(this.activeObj, 'parameter', {})
      this.loadChartMetadata()
      if (this.activeHasDeviceBinding && this.chartDeviceSource === 'page') this.loadChartProducts()
    } }
  },
  beforeDestroy () {
    if (this.chartMetadataCancellation) this.chartMetadataCancellation.cancel()
    if (this.chartProductsCancellation) this.chartProductsCancellation.cancel()
  },
  methods: {
    setChartParameter (key, value) {
      if (!this.activeObj.parameter) this.$set(this.activeObj, 'parameter', {})
      this.$set(this.activeObj.parameter, key, value)
    },
    fixedChartTime (value) {
      try { return queryTime(value, null, Date.now()) } catch (error) { return null }
    },
    selectChartProduct (id) {
      const product = this.chartProducts.find(item => item.id === id)
      this.setChartParameter('productName', product ? product.name : '')
      this.setChartParameter('deviceId', '')
      this.setChartParameter('deviceName', '')
      this.selectChartProperty('')
    },
    selectChartProperty (id) {
      const property = this.chartProperties.find(item => item.id === id)
      this.setChartParameter('property', id)
      this.setChartParameter('propertyName', property ? property.name : '')
      this.setChartParameter('propertyType', property ? property.type : '')
      this.setChartParameter('propertyUnit', property ? property.unit : '')
    },
    async loadChartProducts (force = false) {
      if (this.chartProductsLoading || (this.chartProducts.length && !force)) return
      this.chartProductsLoading = true; this.chartProductsError = ''
      const cancellation = createRequestCancellation()
      this.chartProductsCancellation = cancellation
      try {
        const body = responseBody(await productListNoPage({}, { cancelToken: cancellation.token }))
        if (!this._isDestroyed) this.chartProducts = propertyRows(body)
      } catch (error) {
        if (!this._isDestroyed && !isRequestCanceled(error)) this.chartProductsError = error.message || '产品列表读取失败'
      } finally { if (!this._isDestroyed) this.chartProductsLoading = false }
    },
    async loadChartMetadata () {
      if (this.chartMetadataCancellation) this.chartMetadataCancellation.cancel()
      const key = this.chartSettingsKey
      this.chartProperties = []; this.chartMetadataError = ''; this.chartMetadataLoading = false
      if (!key) return
      const page = this.chartDeviceSource === 'page'
      const id = page ? this.activeParameter.productId : this.activeParameter.deviceId
      if (!id) return
      const cancellation = createRequestCancellation()
      this.chartMetadataCancellation = cancellation
      this.chartMetadataLoading = true
      try {
        const body = responseBody(await (page ? getChartProduct(id, { cancelToken: cancellation.token }) : getChartDevice(id, { cancelToken: cancellation.token })))
        if (this._isDestroyed || key !== this.chartSettingsKey || cancellation !== this.chartMetadataCancellation) return
        this.chartProperties = chartProperties(body.result && body.result.metadata)
        if (!this.chartProperties.length) this.chartMetadataError = '当前物模型没有可选属性'
      } catch (error) {
        if (!this._isDestroyed && key === this.chartSettingsKey && cancellation === this.chartMetadataCancellation && !isRequestCanceled(error)) this.chartMetadataError = error.message || '物模型读取失败'
      } finally {
        if (!this._isDestroyed && key === this.chartSettingsKey && cancellation === this.chartMetadataCancellation) this.chartMetadataLoading = false
      }
    }
  }
}
